/**
 * Validation Enhancements for User Experience
 * 
 * Implements PRD requirements for enhanced form validation UX including:
 * - Required field visual indicators using status icons
 * - Enhanced placeholder text system
 * - Real-time validation feedback
 * - Smart next button management
 */

import './validation-icons.css';
import { FieldInfo, FieldRegistry, getFieldRegistry } from '../field-registry';
import { FieldDetector, getFieldDetector } from '../field-detector';
import { FieldType } from '../detection-patterns';
import { getVisualFeedback, FeedbackState } from '../feedback/visual-feedback';
import { FormatValidators } from './format-validators';
import { debounceValidation } from '../debounce/debounce-utils';

/**
 * Enhanced placeholder text mappings per PRD requirements
 */
export const EnhancedPlaceholders: Record<FieldType, string> = {
  [FieldType.SSN]: 'XXX-XX-XXXX',
  [FieldType.DATE_OF_BIRTH]: 'MM/DD/YYYY',
  [FieldType.PHONE]: 'XXX-XXX-XXXX',
  [FieldType.CURRENCY]: 'Amount in dollars',
  [FieldType.ZIP]: 'XXXXX',
  [FieldType.UNKNOWN]: 'Enter your information'
};

/**
 * Special placeholders for email fields (detected by input type)
 */
export const EMAIL_PLACEHOLDER = 'your@email.com';

/**
 * Configuration for validation enhancements
 */
export interface ValidationEnhancementConfig {
  /** Enable required field visual indicators on page load */
  enableRequiredFieldIndicators?: boolean;
  /** Enable enhanced placeholder text */
  enableEnhancedPlaceholders?: boolean;
  /** Enable real-time validation with 500ms debounce */
  enableRealTimeValidation?: boolean;
  /** Enable smart next button management */
  enableSmartNextButton?: boolean;
  /** CSS selector for next buttons */
  nextButtonSelector?: string;
  /** Custom required field selector */
  requiredFieldSelector?: string;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: Required<ValidationEnhancementConfig> = {
  enableRequiredFieldIndicators: true,
  enableEnhancedPlaceholders: true,
  enableRealTimeValidation: true,
  enableSmartNextButton: true,
  nextButtonSelector: '[data-name="next"], .next-button, button[type="submit"]',
  requiredFieldSelector: '[required], [data-required="true"], [aria-required="true"]'
};

/**
 * Validation enhancement system
 */
export class ValidationEnhancements {
  private config: Required<ValidationEnhancementConfig>;
  private visualFeedback = getVisualFeedback();
  private debouncedValidation: Map<HTMLElement, Function> = new Map();
  private validationState: Map<HTMLElement, boolean> = new Map();
  private nextButtons: HTMLElement[] = [];
  private trackedFields: Set<HTMLElement> = new Set();
  private fieldRegistry: FieldRegistry;
  private fieldDetector: FieldDetector;
  private statusIcons: Map<HTMLElement, HTMLElement> = new Map();

  constructor(config: ValidationEnhancementConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.fieldRegistry = getFieldRegistry();
    this.fieldDetector = getFieldDetector();
  }

  /**
   * Initialize validation enhancements for all detected fields
   */
  public initialize(): void {
    console.log('🚀 VALIDATION ENHANCEMENTS INITIALIZING...');
    
    // First, detect and register all form fields
    const scanResult = this.fieldDetector.scanDocument();
    console.log('🔍 Field detection complete:', scanResult);
    
    // Apply required field indicators
    if (this.config.enableRequiredFieldIndicators) {
      console.log('🔴 Initializing required field indicators...');
      this.initializeRequiredFieldIndicators();
    }

    // Apply enhanced placeholders
    if (this.config.enableEnhancedPlaceholders) {
      console.log('📝 Initializing enhanced placeholders...');
      this.initializeEnhancedPlaceholders();
    }

    // Initialize real-time validation
    if (this.config.enableRealTimeValidation) {
      console.log('⚡ Initializing real-time validation...');
      this.initializeRealTimeValidation();
    }

    // Initialize next button management
    if (this.config.enableSmartNextButton) {
      console.log('🔘 Initializing next button management...');
      this.initializeNextButtonManagement();
    }
    
    console.log('✅ VALIDATION ENHANCEMENTS INITIALIZED');
  }

  /**
   * Initialize real-time validation for all detected fields
   */
  private initializeRealTimeValidation(): void {
    // Get all detected fields from the field registry
    const detectedFields = this.fieldRegistry.getAllFields();

    console.log('🔍 Found detected fields:', detectedFields.map(f => ({
      name: f.element.name || f.element.id,
      type: f.fieldType,
      hasRequired: f.element.hasAttribute('required')
    })));

    // Also find all form input fields that might not be detected by the formatter
    // but still need validation (like first_name, last_name, email, checkboxes, etc.)
    const allFormInputs = document.querySelectorAll('input[type="text"], input[type="email"], textarea, input[type="checkbox"], input[type="radio"]') as NodeListOf<HTMLInputElement | HTMLTextAreaElement>;

    console.log('🔍 Found all form inputs:', Array.from(allFormInputs).map(el => ({
      name: el.name || el.id,
      type: el.type,
      hasRequired: el.hasAttribute('required')
    })));
    
    // Set up validation for detected fields
    console.log('🔍 Initializing detected fields:', detectedFields.map(f => ({
      name: f.element.name || f.element.id,
      type: f.element.type,
      fieldType: f.fieldType
    })));
    
    for (const field of detectedFields) {
      this.initializeField(field); // Use full initialization instead of just setupRealTimeValidation
    }
    
    // Set up validation for additional form inputs not caught by field detector
    for (const input of allFormInputs) {
      // Skip if already handled by detected fields
      const alreadyDetected = detectedFields.some(f => f.element === input);
      if (alreadyDetected) {
        continue;
      }
      
      // Create a basic FieldInfo for undetected inputs
      const fieldInfo: FieldInfo = {
        element: input,
        dataName: input.name || input.getAttribute('data-name') || null,
        fieldType: FieldType.UNKNOWN,
        detection: {
          fieldType: FieldType.UNKNOWN,
          method: 'manual_validation',
          confidence: 1.0,
          requiresFormatting: false
        },
        currentValue: input.value,
        isActive: false,
        detectedAt: new Date()
      };
      
      console.log('🔍 Adding manual field for validation:', input.name || input.id);
      this.initializeField(fieldInfo); // Use full initialization
    }
  }

  /**
   * Initialize validation enhancements for a specific field
   */
  public initializeField(field: FieldInfo): void {
    console.log('🎯 initializeField called for:', field.element.name || field.element.id || 'unnamed field');
    
    if (this.trackedFields.has(field.element)) {
      console.log('⏭️ Field already initialized, skipping');
      return; // Already initialized
    }

    console.log('✅ Adding field to tracked fields');
    this.trackedFields.add(field.element);

    // Setup status icon for text input fields (NOT checkboxes or radios)
    if (field.element instanceof HTMLInputElement &&
        field.element.type !== 'checkbox' &&
        field.element.type !== 'radio') {
      this.setupFieldWithIcon(field.element);
      // Show grey neutral icon by default
      this.updateStatusIcon(field.element, 'neutral', true); // Keep grey icon visible
    }

    // Apply required field indicator if field is required
    if (this.config.enableRequiredFieldIndicators && this.isFieldRequired(field.element)) {
      this.applyRequiredFieldIndicator(field);
    }

    // Apply enhanced placeholder
    if (this.config.enableEnhancedPlaceholders) {
      this.applyEnhancedPlaceholder(field);
    }

    // Set up real-time validation
    if (this.config.enableRealTimeValidation) {
      this.setupRealTimeValidation(field);
    }

    // Set up email enhancements AFTER status icon setup for all email fields
    if (field.element instanceof HTMLInputElement && field.element.type === 'email') {
      this.setupEmailEnhancements(field.element);
    }

    // Initialize validation state based on field requirements
    // Required fields start as invalid, optional fields start as valid
    // This prevents red borders on page load but maintains accurate state for Next button
    const isRequired = this.isFieldRequired(field.element);

    if (isRequired) {
      // All required fields start as invalid until user fills them correctly
      // This ensures Next button stays disabled until all required fields are valid
      this.validationState.set(field.element, false);
    } else {
      // Optional fields start as valid
      this.validationState.set(field.element, true);
    }
  }

  /**
   * Initialize required field tracking (NO red borders on page load)
   */
  private initializeRequiredFieldIndicators(): void {
    const requiredFields = document.querySelectorAll(this.config.requiredFieldSelector);
    
    requiredFields.forEach((element) => {
      if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement) {
        // Skip select elements as specified in existing system
        if (element instanceof HTMLSelectElement) {
          return;
        }

        const inputElement = element as HTMLInputElement;
        
        // Store initial state as invalid (required but empty) but NO visual styling
        // Red borders only appear after user interaction
        this.validationState.set(inputElement, false);
      }
    });
  }

  /**
   * Apply enhanced placeholder text to fields
   */
  private initializeEnhancedPlaceholders(): void {
    // This will be called per field via initializeField
  }

  /**
   * Initialize required field tracking
   */
  private applyRequiredFieldIndicator(field: FieldInfo): void {
    // Icon setup is now handled in initializeField for all fields
    // Just mark required fields as initially invalid in validation state
    this.validationState.set(field.element, false);
  }

  /**
   * Apply enhanced placeholder text to a field
   */
  private applyEnhancedPlaceholder(field: FieldInfo): void {
    if (!(field.element instanceof HTMLInputElement)) {
      return;
    }

    // Handle email fields specially
    if (field.element.type === 'email') {
      field.element.placeholder = EMAIL_PLACEHOLDER;
      return;
    }

    // Handle field-specific placeholders for currency fields
    if (field.fieldType === FieldType.CURRENCY) {
      const fieldName = field.element.name || field.element.getAttribute('data-name') || '';
      if (fieldName.includes('housing_payment') || fieldName.includes('housing-payment')) {
        field.element.placeholder = 'Monthly housing payment in dollars';
        return;
      } else if (fieldName.includes('income')) {
        field.element.placeholder = 'Pre-tax Yearly income in dollars';
        return;
      }
    }

    // Handle other field types
    const placeholder = EnhancedPlaceholders[field.fieldType];
    if (placeholder) {
      field.element.placeholder = placeholder;
    }
  }

  /**
   * Set up real-time validation for a field with 500ms debounce
   */
  private setupRealTimeValidation(field: FieldInfo): void {
    const element = field.element;
    let hasInteracted = false;
    let errorMessageTimer: number | null = null;

    // Create debounced border validation (immediate red border)
    const debouncedBorderValidate = debounceValidation(() => {
      if (hasInteracted) {
        this.validateFieldBorderOnly(field);
      }
    }, { delay: 200 }); // Quick border feedback

    // Create debounced message validation (delayed error message)
    const debouncedMessageValidate = debounceValidation(() => {
      if (hasInteracted) {
        this.validateFieldWithMessage(field);
      }
    }, { delay: 1000 }); // 1 second delay for error messages

    this.debouncedValidation.set(element, debouncedMessageValidate);

    // Add event listeners
    element.addEventListener('input', () => {
      hasInteracted = true;

      // Clear any existing error message immediately
      this.clearFieldErrorMessage(element);

      // Update border quickly
      debouncedBorderValidate();

      // Schedule error message with delay
      debouncedMessageValidate();
    });

    element.addEventListener('blur', () => {
      hasInteracted = true;
      // Skip validation for checkboxes/radios on blur (they use change events)
      if (element instanceof HTMLInputElement && (element.type === 'checkbox' || element.type === 'radio')) {
        return;
      }
      // On blur, show full validation immediately
      this.validateFieldWithMessage(field);
    });

    element.addEventListener('focus', () => {
      // Skip for checkboxes/radios
      if (element instanceof HTMLInputElement && (element.type === 'checkbox' || element.type === 'radio')) {
        return;
      }
      // Clear any existing error messages on focus
      this.clearFieldErrorMessage(element);
    });

    // CRITICAL: Add change event listener for checkboxes and radio buttons
    // Checkboxes/radios don't fire 'input' events, they fire 'change' events
    // When these change, we need to re-check if Next button should be enabled
    if (element instanceof HTMLInputElement && (element.type === 'checkbox' || element.type === 'radio')) {
      element.addEventListener('change', () => {
        // Mark checkbox/radio as valid when checked (they're typically optional or just need to be checked)
        if ((element as HTMLInputElement).checked) {
          this.validationState.set(element, true);
        } else {
          // If unchecked and required, mark as invalid
          if (this.isFieldRequired(element)) {
            this.validationState.set(element, false);
          } else {
            this.validationState.set(element, true);
          }
        }

        // CRITICAL: Update Next button state to re-evaluate ALL fields
        // This ensures that if SSN is still invalid, button stays disabled
        this.updateNextButtonState();
      });
    }
  }

  /**
   * Validate a field and update visual feedback with status icons
   */
  private validateField(field: FieldInfo): void {
    const element = field.element;
    const value = element instanceof HTMLInputElement ? element.value : '';

    // Skip validation if field is empty and not required
    if (!value.trim() && !this.isFieldRequired(element)) {
      this.clearFieldErrorMessage(element);
      if (element instanceof HTMLInputElement) {
        this.updateStatusIcon(element, 'neutral', true); // Keep grey icon visible
      }
      this.validationState.set(element, true);
      this.updateNextButtonState();
      return;
    }

    // If empty but required, show neutral icon (user hasn't provided input yet)
    if (!value.trim() && this.isFieldRequired(element)) {
      if (element instanceof HTMLInputElement) {
        this.updateStatusIcon(element, 'neutral', true);
      }
      this.validationState.set(element, false);
      this.updateNextButtonState();
      return;
    }

    // Perform validation - check for email fields first, then general text validation
    let result;
    if (element instanceof HTMLInputElement && element.type === 'email') {
      result = FormatValidators.validateEmail(value);
    } else if (field.fieldType !== FieldType.UNKNOWN) {
      // Pass field context for currency validation
      const fieldName = element.name || element.getAttribute('data-name') || element.id || '';
      const context = {
        fieldType: field.fieldType,
        value: value,
        rawValue: value,
        fieldName: fieldName
      };
      result = FormatValidators.validateByFieldType(field.fieldType, value, {}, context);
    } else {
      // For basic text fields (first name, last name, etc), just check if not empty
      result = {
        isValid: value.trim().length > 0,
        errors: value.trim().length === 0 ? ['This field is required'] : [],
        warnings: []
      };
    }

    // Debug logging
    console.log('🔍 Validation Debug:', {
      fieldName: element.name || element.id,
      fieldType: field.fieldType,
      value: value,
      trimmedValue: value.trim(),
      isValid: result.isValid,
      errors: result.errors
    });

    // Update visual feedback with status icons
    if (result.isValid) {
      console.log('✅ Clearing validation for field:', element.name || element.id);
      this.clearFieldErrorMessage(element);
      if (element instanceof HTMLInputElement) {
        this.updateStatusIcon(element, 'valid', true);
      }
      this.validationState.set(element, true);
      
      // Show warnings even for valid fields
      if (result.warnings && result.warnings.length > 0) {
        console.log('⚠️ Showing warning for field:', element.name || element.id);
        // Show domain suggestions for email fields
        if (element instanceof HTMLInputElement && element.type === 'email') {
          this.handleEmailWarnings(element, result.warnings);
        }
      }
    } else {
      console.log('❌ Setting error for field:', element.name || element.id);
      if (element instanceof HTMLInputElement) {
        this.updateStatusIcon(element, 'error', true);
      }
      // Don't show text error messages - red icon is sufficient visual feedback
      this.validationState.set(element, false);
    }

    // Update next button state
    this.updateNextButtonState();
  }

  /**
   * Validate field and update status icon only (no error message)
   */
  private validateFieldBorderOnly(field: FieldInfo): void {
    const element = field.element;
    const value = element instanceof HTMLInputElement ? element.value : '';

    // Skip validation if field is empty and not required, but keep grey icon visible
    if (!value.trim() && !this.isFieldRequired(element)) {
      if (element instanceof HTMLInputElement) {
        this.updateStatusIcon(element, 'neutral', true); // Keep grey icon visible
      }
      this.validationState.set(element, true);
      this.updateNextButtonState();
      return;
    }

    // If empty but required, show neutral icon (user hasn't provided input yet)
    if (!value.trim() && this.isFieldRequired(element)) {
      if (element instanceof HTMLInputElement) {
        this.updateStatusIcon(element, 'neutral', true);
      }
      this.validationState.set(element, false);
      this.updateNextButtonState();
      return;
    }

    // Perform validation
    let result;
    if (element instanceof HTMLInputElement && element.type === 'email') {
      result = FormatValidators.validateEmail(value);
    } else if (field.fieldType !== FieldType.UNKNOWN) {
      result = FormatValidators.validateByFieldType(field.fieldType, value);
    } else {
      result = {
        isValid: value.trim().length > 0,
        errors: value.trim().length === 0 ? ['This field is required'] : [],
        warnings: []
      };
    }

    // Update icon based on validation result
    if (element instanceof HTMLInputElement) {
      if (result.isValid) {
        this.updateStatusIcon(element, 'valid', true);
        this.validationState.set(element, true);
      } else {
        this.updateStatusIcon(element, 'error', true);
        this.validationState.set(element, false);
      }
    }

    this.updateNextButtonState();
  }

  /**
   * Validate field and show error message (full validation)
   */
  private validateFieldWithMessage(field: FieldInfo): void {
    // First update the border
    this.validateFieldBorderOnly(field);
    
    const element = field.element;
    const value = element instanceof HTMLInputElement ? element.value : '';

    // Only show error message if field has invalid content
    if (value.trim() || this.isFieldRequired(element)) {
      // Perform validation to get error message
      let result;
      if (element instanceof HTMLInputElement && element.type === 'email') {
        result = FormatValidators.validateEmail(value);
      } else if (field.fieldType !== FieldType.UNKNOWN) {
        // Pass field context for currency validation
        const fieldName = element.name || element.getAttribute('data-name') || element.id || '';
        const context = {
          fieldType: field.fieldType,
          value: value,
          rawValue: value,
          fieldName: fieldName
        };
        result = FormatValidators.validateByFieldType(field.fieldType, value, {}, context);
      } else {
        result = {
          isValid: value.trim().length > 0,
          errors: value.trim().length === 0 ? ['This field is required'] : [],
          warnings: []
        };
      }

      // Don't show text error messages - icons provide sufficient visual feedback
      if (!result.isValid && result.errors.length > 0) {
        console.log('❌ Field invalid, showing red icon only (no text message):', element.name || element.id);
        // CRITICAL: Actually update the icon to red when field is invalid!
        if (element instanceof HTMLInputElement) {
          this.updateStatusIcon(element, 'error', true);
        }
      }
      // Don't show warning text messages either
      else if (result.isValid && result.warnings && result.warnings.length > 0) {
        console.log('⚠️ Field has warnings, but is valid - showing green icon:', element.name || element.id);
        // Show valid status on icon (warnings don't make field invalid)
        if (element instanceof HTMLInputElement) {
          this.updateStatusIcon(element, 'valid', true); // Show green for valid with warnings
          
          // Show domain suggestions for email fields
          if (element.type === 'email') {
            this.handleEmailWarnings(element, result.warnings);
          }
        }
      }
    }
  }

  /**
   * Show simple error message below field
   */
  private showSimpleErrorMessage(element: HTMLElement, errorMessage: string): void {
    // Clear any existing error message first
    this.clearFieldErrorMessage(element);

    // Create simple error message element
    const errorElement = document.createElement('div');
    errorElement.className = 'validation-error-message';
    errorElement.style.cssText = `
      color: #E53E3E;
      font-size: 14px;
      margin-top: 4px;
      display: block;
    `;
    errorElement.textContent = errorMessage;

    // Insert after the field
    element.parentNode?.insertBefore(errorElement, element.nextSibling);
  }

  /**
   * Clear error message for a field
   */
  private clearFieldErrorMessage(element: HTMLElement): void {
    const errorMessage = element.parentNode?.querySelector('.validation-error-message');
    if (errorMessage) {
      errorMessage.remove();
    }
  }

  /**
   * Initialize next button management
   */
  private initializeNextButtonManagement(): void {
    // Find all next buttons
    const buttons = document.querySelectorAll(this.config.nextButtonSelector);
    
    buttons.forEach((button) => {
      if (button instanceof HTMLElement) {
        this.nextButtons.push(button);
        this.setupNextButton(button);
      }
    });

    // Initial state update
    this.updateNextButtonState();
  }

  /**
   * Set up a next button with hover tooltips
   */
  private setupNextButton(button: HTMLElement): void {
    // Create tooltip element
    const tooltip = document.createElement('div');
    tooltip.className = 'formatting-tooltip formatting-hidden';
    tooltip.style.cssText = `
      position: absolute;
      z-index: 1000;
      padding: 8px 12px;
      background-color: rgba(0, 0, 0, 0.9);
      color: white;
      border-radius: 4px;
      font-size: 14px;
      line-height: 1.4;
      max-width: 250px;
      word-wrap: break-word;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      pointer-events: none;
    `;
    
    button.appendChild(tooltip);

    // Add hover events
    button.addEventListener('mouseenter', () => {
      this.showButtonTooltip(button, tooltip);
    });

    button.addEventListener('mouseleave', () => {
      tooltip.classList.add('formatting-hidden');
    });

    // Prevent submission when disabled
    button.addEventListener('click', (e) => {
      if (button.hasAttribute('disabled') || button.classList.contains('disabled')) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });
  }

  /**
   * Show tooltip for next button based on current validation state
   */
  private showButtonTooltip(button: HTMLElement, tooltip: HTMLElement): void {
    const invalidFields = this.getInvalidFields();
    let message = '';

    if (invalidFields.length === 0) {
      message = "Great! You're ready to continue to the next step.";
    } else {
      // Get field names for more helpful messaging
      const fieldNames = invalidFields.map(field => this.getFieldDisplayName(field)).filter(name => name);
      
      if (fieldNames.length === 1) {
        message = `Almost there! Please fill out your ${fieldNames[0].toLowerCase()} to continue.`;
      } else if (fieldNames.length === 2) {
        message = `Just need your ${fieldNames[0].toLowerCase()} and ${fieldNames[1].toLowerCase()} to continue.`;
      } else if (fieldNames.length > 2) {
        message = `Please complete the ${fieldNames.length} required fields above to continue.`;
      } else {
        // Fallback if we can't get field names
        message = invalidFields.length === 1 
          ? 'Please complete the required field to continue.'
          : `Please complete ${invalidFields.length} required fields to continue.`;
      }
    }

    tooltip.textContent = message;
    tooltip.classList.remove('formatting-hidden');

    // Position tooltip above button
    const rect = button.getBoundingClientRect();
    tooltip.style.bottom = '100%';
    tooltip.style.left = '50%';
    tooltip.style.transform = 'translateX(-50%)';
    tooltip.style.marginBottom = '8px';
  }

  /**
   * Get display name for a field based on labels or data attributes
   */
  private getFieldDisplayName(element: HTMLElement): string {
    // Try to find associated label
    const fieldId = element.id;
    if (fieldId) {
      const label = document.querySelector(`label[for="${fieldId}"]`);
      if (label?.textContent) {
        return label.textContent.replace('*', '').trim();
      }
    }

    // Try to find label by proximity (common Webflow pattern)
    const parentElement = element.parentElement;
    if (parentElement) {
      const siblingLabel = parentElement.querySelector('label');
      if (siblingLabel?.textContent) {
        return siblingLabel.textContent.replace('*', '').trim();
      }

      // Look for text content in parent that might be the field label
      const textNodes = Array.from(parentElement.childNodes)
        .filter(node => node.nodeType === Node.TEXT_NODE && node.textContent?.trim())
        .map(node => node.textContent?.trim())
        .filter(text => text && text.length > 1);

      if (textNodes.length > 0 && textNodes[0]) {
        return textNodes[0].replace('*', '');
      }
    }

    // Try data-name attribute
    const dataName = element.getAttribute('data-name');
    if (dataName) {
      return dataName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }

    // Try placeholder as last resort
    if (element instanceof HTMLInputElement && element.placeholder) {
      return element.placeholder;
    }

    return ''; // Return empty string if no name found
  }

  /**
   * Update next button state based on validation results
   */
  private updateNextButtonState(): void {
    const invalidFields = this.getInvalidFields();
    const hasInvalidFields = invalidFields.length > 0;

    // DEBUG: Log validation state for debugging
    console.log('🔘 Updating Next button state:');
    console.log('   Invalid fields count:', invalidFields.length);
    console.log('   Invalid fields:', invalidFields.map(f => {
      const input = f as HTMLInputElement;
      return {
        name: input.name || input.id,
        type: input.type,
        value: input.value,
        validationState: this.validationState.get(f)
      };
    }));
    console.log('   All tracked fields:', Array.from(this.validationState.entries()).map(([el, isValid]) => {
      const input = el as HTMLInputElement;
      return {
        name: input.name || input.id,
        type: input.type,
        isValid: isValid
      };
    }));

    this.nextButtons.forEach((button) => {
      if (hasInvalidFields) {
        // Disable button
        console.log('   ❌ Disabling Next button');
        button.setAttribute('disabled', 'true');
        button.classList.add('disabled');
        button.style.opacity = '0.6';
        button.style.cursor = 'not-allowed';
      } else {
        // Enable button
        console.log('   ✅ Enabling Next button');
        button.removeAttribute('disabled');
        button.classList.remove('disabled');
        button.style.opacity = '1';
        button.style.cursor = 'pointer';
      }
    });
  }

  /**
   * Get list of invalid fields
   */
  private getInvalidFields(): HTMLElement[] {
    const invalidFields: HTMLElement[] = [];
    
    this.validationState.forEach((isValid, element) => {
      if (!isValid) {
        invalidFields.push(element);
      }
    });

    return invalidFields;
  }

  /**
   * Check if field is required
   */
  private isFieldRequired(element: HTMLElement): boolean {
    return element.hasAttribute('required') || 
           element.getAttribute('data-required') === 'true' ||
           element.getAttribute('aria-required') === 'true';
  }

  /**
   * Add a field to validation tracking
   */
  public addField(field: FieldInfo): void {
    this.initializeField(field);
  }

  /**
   * Remove a field from validation tracking
   */
  public removeField(field: FieldInfo): void {
    const element = field.element;
    
    // Remove from tracking
    this.trackedFields.delete(element);
    this.validationState.delete(element);
    
    // Remove debounced validation
    const debouncedFn = this.debouncedValidation.get(element);
    if (debouncedFn && typeof debouncedFn === 'function' && 'cancel' in debouncedFn) {
      (debouncedFn as any).cancel();
    }
    this.debouncedValidation.delete(element);

    // Clear visual feedback
    this.visualFeedback.clearFeedback(field);

    // Update next button state
    this.updateNextButtonState();
  }

  /**
   * Create status icon for a field
   */
  private createStatusIcon(fieldElement: HTMLInputElement): HTMLElement {
    const icon = document.createElement('div');
    icon.className = 'field-status-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.setAttribute('data-field-status', 'neutral');
    
    return icon;
  }

  /**
   * Setup field with status icon
   */
  private setupFieldWithIcon(fieldElement: HTMLInputElement): void {
    // CRITICAL: Never add icons to checkboxes or radio buttons
    if (fieldElement.type === 'checkbox' || fieldElement.type === 'radio') {
      return;
    }

    console.log('🔍 Setting up icon for field:', fieldElement.name || fieldElement.id || 'unnamed');

    // Skip if already has icon
    if (this.statusIcons.has(fieldElement)) {
      console.log('⏭️ Field already has icon, skipping');
      return;
    }

    // Create wrapper if field doesn't have one
    const parent = fieldElement.parentElement;
    if (!parent?.classList.contains('field-with-status-icon')) {
      console.log('🎁 Creating wrapper for field');
      const wrapper = document.createElement('div');
      wrapper.className = 'field-with-status-icon';
      
      // Insert wrapper before field
      fieldElement.parentNode?.insertBefore(wrapper, fieldElement);
      
      // Move field into wrapper
      wrapper.appendChild(fieldElement);
    } else {
      console.log('✅ Field already has wrapper');
    }

    // Create and add icon
    const icon = this.createStatusIcon(fieldElement);
    const wrapper = fieldElement.parentElement!;
    wrapper.appendChild(icon);

    console.log('🎯 Icon created and added to field:', fieldElement.name || fieldElement.id);

    // Store reference
    this.statusIcons.set(fieldElement, icon);
  }

  /**
   * Update status icon for a field
   */
  private updateStatusIcon(fieldElement: HTMLInputElement, status: 'neutral' | 'error' | 'valid', isVisible: boolean = true): void {
    // CRITICAL: Never update icons for checkboxes or radio buttons
    if (fieldElement.type === 'checkbox' || fieldElement.type === 'radio') {
      return;
    }

    console.log('🎨 Updating icon for field:', fieldElement.name || fieldElement.id, 'Status:', status, 'Visible:', isVisible);

    let icon = this.statusIcons.get(fieldElement);
    if (!icon) {
      console.warn('❌ No icon found for field, creating one now:', fieldElement.name || fieldElement.id);
      // Create icon on-demand since initialization failed
      this.setupFieldWithIcon(fieldElement);
      icon = this.statusIcons.get(fieldElement);
      
      if (!icon) {
        console.error('❌ Failed to create icon for field:', fieldElement.name || fieldElement.id);
        return;
      }
    }

    // Remove existing status classes
    icon.classList.remove('status-neutral', 'status-error', 'status-valid', 'visible');
    
    // Add new status class
    icon.classList.add(`status-${status}`);
    
    // Show/hide icon
    if (isVisible) {
      icon.classList.add('visible');
    }

    console.log('✅ Icon updated. Classes:', icon.className);

    // Debug: Check actual DOM position and styles
    const rect = icon.getBoundingClientRect();
    const computedStyle = window.getComputedStyle(icon);
    
    console.log('📐 Icon position:', {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
      visible: rect.width > 0 && rect.height > 0
    });
    console.log('🎨 Computed styles:', {
      position: computedStyle.position,
      opacity: computedStyle.opacity,
      display: computedStyle.display,
      visibility: computedStyle.visibility,
      zIndex: computedStyle.zIndex,
      backgroundColor: computedStyle.backgroundColor,
      transform: computedStyle.transform
    });

    // Update aria attribute
    icon.setAttribute('data-field-status', status);

    // Add ARIA live region for screen readers on status changes
    if (status === 'error') {
      icon.setAttribute('aria-label', 'Field has validation error');
    } else if (status === 'valid') {
      icon.setAttribute('aria-label', 'Field is valid');
    } else {
      icon.setAttribute('aria-label', 'Field status neutral');
    }
  }

  /**
   * Get current validation state
   */
  public getValidationState(): Map<HTMLElement, boolean> {
    return new Map(this.validationState);
  }

  /**
   * Force validation of all tracked fields
   */
  public validateAll(): boolean {
    this.trackedFields.forEach((element) => {
      // Find field info for element - this would need to be implemented
      // For now, we'll trigger validation through the existing system
    });

    return this.getInvalidFields().length === 0;
  }

  /**
   * Set up email field enhancements (domain suggestions and tips tooltip)
   */
  private setupEmailEnhancements(element: HTMLInputElement): void {
    console.log('🚀 Setting up email enhancements for:', element.name || element.id);
    
    const wrapper = element.parentElement;
    if (!wrapper) {
      console.log('❌ No wrapper found for email field');
      return;
    }

    console.log('📦 Email field wrapper:', wrapper.className);

    // Add info icon for email tips
    this.createEmailInfoIcon(wrapper, element);

    // Set up domain suggestion handling
    this.setupDomainSuggestions(wrapper, element);
    
    console.log('✅ Email enhancements setup completed');
  }

  /**
   * Create email info icon ('i' icon) with tips tooltip
   */
  private createEmailInfoIcon(wrapper: HTMLElement, element: HTMLInputElement): void {
    // Check if info icon already exists
    if (wrapper.querySelector('.email-info-icon')) return;

    const infoIcon = document.createElement('div');
    infoIcon.className = 'email-info-icon';
    
    // Create tips tooltip
    const tipsTooltip = document.createElement('div');
    tipsTooltip.className = 'email-tips-tooltip';
    tipsTooltip.innerHTML = `
      <div class="tips-title">Email Tips</div>
      <ul class="tips-list">
        <li>Use your most frequently checked email</li>
        <li>Double-check spelling before submitting</li>
        <li>Avoid temporary or disposable email services</li>
        <li>Make sure you can receive emails at this address</li>
      </ul>
    `;

    // Add event listeners for tooltip
    let hideTimeout: ReturnType<typeof setTimeout>;
    
    infoIcon.addEventListener('mouseenter', () => {
      clearTimeout(hideTimeout);
      tipsTooltip.classList.add('visible');
    });

    infoIcon.addEventListener('mouseleave', () => {
      hideTimeout = setTimeout(() => {
        tipsTooltip.classList.remove('visible');
      }, 150);
    });

    // Touch support for mobile
    infoIcon.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      tipsTooltip.classList.toggle('visible');
      
      // Hide after 5 seconds on mobile
      setTimeout(() => {
        tipsTooltip.classList.remove('visible');
      }, 5000);
    });

    wrapper.appendChild(infoIcon);
    wrapper.appendChild(tipsTooltip);
  }

  /**
   * Set up domain suggestion functionality
   */
  private setupDomainSuggestions(wrapper: HTMLElement, element: HTMLInputElement): void {
    // Create suggestion tooltip container
    const suggestionTooltip = document.createElement('div');
    suggestionTooltip.className = 'email-suggestion-tooltip';
    wrapper.appendChild(suggestionTooltip);

    // Store reference for easy access
    (element as any).suggestionTooltip = suggestionTooltip;
  }

  /**
   * Show domain correction suggestion
   */
  public showDomainSuggestion(element: HTMLInputElement, suggestion: string): void {
    const tooltip = (element as any).suggestionTooltip;
    if (!tooltip) return;

    // Extract corrected email from suggestion message
    const match = suggestion.match(/Did you mean (.+)\?/);
    if (!match) return;
    
    const correctedEmail = match[1];
    
    tooltip.innerHTML = `
      <div class="suggestion-text">${suggestion}</div>
      <div class="suggested-email" data-email="${correctedEmail}">
        ${correctedEmail}
      </div>
    `;

    // Add click handler for suggestion
    const suggestionButton = tooltip.querySelector('.suggested-email') as HTMLElement;
    if (suggestionButton) {
      suggestionButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        console.log('🖱️ Suggestion clicked, applying:', correctedEmail);
        element.value = correctedEmail;
        
        // Trigger validation events
        element.dispatchEvent(new Event('input', { bubbles: true }));
        element.dispatchEvent(new Event('change', { bubbles: true }));
        element.dispatchEvent(new Event('blur', { bubbles: true }));
        
        this.hideDomainSuggestion(element);
        console.log('✅ Email corrected to:', element.value);
        
        // Focus back to element for continued editing
        setTimeout(() => element.focus(), 100);
      });
    }

    tooltip.classList.add('visible');

    // Auto-hide after 8 seconds
    setTimeout(() => {
      this.hideDomainSuggestion(element);
    }, 8000);
  }

  /**
   * Hide domain correction suggestion
   */
  public hideDomainSuggestion(element: HTMLInputElement): void {
    const tooltip = (element as any).suggestionTooltip;
    if (tooltip) {
      tooltip.classList.remove('visible');
    }
  }

  /**
   * Handle email warnings (domain suggestions)
   */
  private handleEmailWarnings(element: HTMLInputElement, warnings: string[]): void {
    // Look for domain correction suggestions
    for (const warning of warnings) {
      if (warning.includes('Did you mean')) {
        console.log('💡 Showing domain suggestion:', warning);
        this.showDomainSuggestion(element, warning);
        break; // Only show first suggestion
      }
    }
  }
}

/**
 * Global validation enhancements instance
 */
let globalValidationEnhancements: ValidationEnhancements | null = null;

/**
 * Get the global validation enhancements instance
 */
export function getValidationEnhancements(config?: ValidationEnhancementConfig): ValidationEnhancements {
  if (!globalValidationEnhancements) {
    globalValidationEnhancements = new ValidationEnhancements(config);
  }
  return globalValidationEnhancements;
}

/**
 * Initialize validation enhancements on DOM ready
 */
export function initializeValidationEnhancements(config?: ValidationEnhancementConfig): ValidationEnhancements {
  const enhancements = getValidationEnhancements(config);
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => enhancements.initialize());
  } else {
    enhancements.initialize();
  }
  
  return enhancements;
}